module Meevo.Plugins.MerchantAccounts {
    "use strict";

    interface ITestConfiguration {
        enabled: boolean;
        objectState?: Model.ObjectStateEnum;
        testCases: ITestCase[];
        merchantProviderId?: Meevo.Model.MerchantAccountProviderEnum;
    }

    interface ITestCase {
        displayName: string;
        action: ProcessorHelperOverrideEnum;
        performProcessorCall: boolean;
        triggerAmount?: number;
        triggerWildcard: string;
        useRequest: ITestCaseOverrides;
        useResponse: ITestCaseOverrides;
    }
    interface ITestCaseOverrides {
        overrides: ITestCaseOverride[];
    }
    interface ITestCaseOverride {
        key: string;
        value: any;
    }
    interface IProviderOptions {
        providerId: Meevo.Model.MerchantAccountProviderEnum;
        canAlwaysTrigger?: boolean;
        canPerformProcessorCall?: boolean;
        canInduceTimeoutBeforeRequest?: boolean;
        canInduceTimeoutAfterRequest?: boolean;
        canInduceHostedPageCaptcha?: boolean;
        canSpecifyTriggerAmount?: boolean;
        canSpecifyTriggerWildcard?: boolean;
        canOverrideRequests?: boolean;
    }
    enum ProcessorHelperOverrideEnum {
        None = 0,
        Sale,
        SaleCardOnFile,
        Refund,
        Reversal,
        Void,
        ActivateDevice,
        DeactivateDevice,
        TipPromptInput,
        TipPromptSelection,
        CreateCardOnFile,
        UpdateCardOnFile,
        DeleteCardOnFile,
        Query,
        AccountQuery,
        SaleAch,
        AutoUpdater,
        MembershipBilling,
        HostedPageSetup,
        HostedPageQuery,
        HostedPagePayment,
        HostedPagePaymentRetry,
        HostedPageFundingSource,
        Capture,
    };

    /**
    * Devices Section of Merchant Account
    */
    export class TestConfigurationSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<ITestConfiguration> {

        public model: ITestConfiguration = {
            enabled: false,
            objectState: Model.ObjectStateEnum.Active,
            testCases: []
        };
        public allAvailableActions: any[] = [
            { name: "", value: ProcessorHelperOverrideEnum.None },
            { name: "Sale", value: ProcessorHelperOverrideEnum.Sale },
            { name: "Sale Card-On-File", value: ProcessorHelperOverrideEnum.SaleCardOnFile },
            { name: "Refund", value: ProcessorHelperOverrideEnum.Refund },
            { name: "Reverse", value: ProcessorHelperOverrideEnum.Reversal },
            { name: "Void", value: ProcessorHelperOverrideEnum.Void },
            { name: "Create Card-On-File", value: ProcessorHelperOverrideEnum.CreateCardOnFile },
            { name: "Update Card-On-File", value: ProcessorHelperOverrideEnum.UpdateCardOnFile },
            { name: "Delete Card-On-File", value: ProcessorHelperOverrideEnum.DeleteCardOnFile },
            { name: "TipPrompt Selection", value: ProcessorHelperOverrideEnum.TipPromptSelection },
            { name: "TipPrompt Input", value: ProcessorHelperOverrideEnum.TipPromptInput },
            { name: "Activate Device", value: ProcessorHelperOverrideEnum.ActivateDevice },
            { name: "Deactivate Device", value: ProcessorHelperOverrideEnum.DeactivateDevice },
            { name: "Sale ACH", value: ProcessorHelperOverrideEnum.SaleAch },
            { name: "Query", value: ProcessorHelperOverrideEnum.Query },
            { name: "Membership Billing", value: ProcessorHelperOverrideEnum.MembershipBilling },
            { name: "Account Query", value: ProcessorHelperOverrideEnum.AccountQuery },
            { name: "Auto Updater", value: ProcessorHelperOverrideEnum.AutoUpdater },
            { name: "Hosted Page Setup", value: ProcessorHelperOverrideEnum.HostedPageSetup },
            { name: "Hosted Page Query", value: ProcessorHelperOverrideEnum.HostedPageQuery },
            { name: "Hosted Page Payment", value: ProcessorHelperOverrideEnum.HostedPagePayment },
            { name: "Hosted Page Payment Retry", value: ProcessorHelperOverrideEnum.HostedPagePaymentRetry },
            { name: "Hosted Page Funding Source", value: ProcessorHelperOverrideEnum.HostedPageFundingSource },
            { name: "Capture", value: ProcessorHelperOverrideEnum.Capture },
        ];
        public availableActions: any[];
        public readonly responseOverrideValueOther = "Other";

        private readonly ProvidersWorldpay = [
            Model.MerchantAccountProviderEnum.Worldpay,
        ];
        private readonly ProvidersMeevoPay = [
            Model.MerchantAccountProviderEnum.MeevoPay,
            Model.MerchantAccountProviderEnum.MeevoPayCanada
        ];
        private readonly ProvidersCareCredit = [
            Model.MerchantAccountProviderEnum.CareCredit,
        ];
        private readonly ProvidersCardConnect = [
            Model.MerchantAccountProviderEnum.CardConnect,
        ];

        public allAvailableResponseOverrides: any[] = [
            {
                name: "Amount",
                dataType: "number",
                providers: this.ProvidersWorldpay
            },
            {
                name: "ApplicationIdentifier",
                dataType: "string",
                providers: this.ProvidersWorldpay
            },
            {
                name: "ApplicationLabel",
                dataType: "string",
                providers: this.ProvidersWorldpay
            },
            {
                name: "ApplicationPreferredName",
                dataType: "string",
                providers: this.ProvidersWorldpay
            },
            {
                name: "CardHolderName",
                dataType: "string",
                providers: [ ...this.ProvidersWorldpay, ...this.ProvidersMeevoPay ]
            },
            {
                name: "CardNumber",
                dataType: "string",
                providers: [ ...this.ProvidersWorldpay, ...this.ProvidersMeevoPay ]
            },
            {
                name: "CardType",
                dataType: "list",
                values: ["Amex", "Credit Card", "Debit", "Diners Club", "Discover", "Mastercard", "Visa"],
                providers: [ ...this.ProvidersWorldpay, ...this.ProvidersMeevoPay ]
            },
            {
                name: "EntryMode",
                dataType: "list",
                values: ["KEYED", "CHIP", "CONTACTLESS", "UNKNOWN"],
                providers: this.ProvidersWorldpay
            },
            {
                name: "ExpirationMonth",
                dataType: "number",
                providers: [ ...this.ProvidersWorldpay, ...this.ProvidersMeevoPay ]
            },
            {
                name: "ExpirationYear",
                dataType: "number",
                providers: [ ...this.ProvidersWorldpay, ...this.ProvidersMeevoPay ]
            },
            {
                name: "ExternalTerminalId",
                dataType: "string",
                providers: this.ProvidersWorldpay
            },
            {
                name: "PaymentType",
                dataType: "list",
                values: [ "Credit", "Debit", "Gift", "Unknown", "None" ],
                providers: this.ProvidersWorldpay
            },
            {
                name: "ResponseCode",
                dataType: "list",
                values: [ "SUCCESS", "FAIL", "TIMEOUT", "DECLINED", "CANCELLED", "20" ],
                providers: this.ProvidersWorldpay
            },
            {
                name: "ResponseJson",
                dataType: "string",
                providers: [
                    ...this.ProvidersWorldpay,
                    ...this.ProvidersMeevoPay,
                    ...this.ProvidersCareCredit,
                    ...this.ProvidersCardConnect,
                ]
            },
            {
                name: "SerialNumber",
                dataType: "string",
                providers: this.ProvidersWorldpay
            },
            {
                name: "TipAmount",
                dataType: "number",
                providers: this.ProvidersWorldpay
            },
            {
                name: "TransactionId",
                dataType: "number",
                providers: this.ProvidersWorldpay
            },
            {
                name: "FundingSource",
                dataType: "list",
                values: [ "CREDIT", "DEBIT", "PREPAID", this.responseOverrideValueOther ],
                providers: this.ProvidersMeevoPay
            },
            {
                name: "TransactionStatus",
                dataType: "list",
                values: [ "APPROVED", "BAD_TID", "CALL FOR AUTH", "DECLINED", "ERROR", this.responseOverrideValueOther ],
                providers: this.ProvidersCareCredit
            },
        ];
        public availableResponseOverrides: any[];
        public availableRequestOverrides: any[] = [
            { name: "AccountId", dataType: "string" },
            { name: "ActivationCode", dataType: "string" },
            { name: "Amount", dataType: "number" },
            { name: "BillingZipCode", dataType: "string" },
            { name: "ExternalTerminalId", dataType: "string" },
            { name: "InvokeManualEntry", dataType: "boolean" },
            { name: "PaymentAccountId", dataType: "string" },
            { name: "ReferenceNumber", dataType: "string" },
            { name: "RequestJson", dataType: "string" },
        ];

        public providerOptionsConfiguration: IProviderOptions[] = [{
            providerId: Meevo.Model.MerchantAccountProviderEnum.Vantiv,
            canAlwaysTrigger: true,
            canInduceHostedPageCaptcha: true,
            canInduceTimeoutBeforeRequest: true,
            canInduceTimeoutAfterRequest: true,
            canOverrideRequests: true,
            canPerformProcessorCall: true,
            canSpecifyTriggerAmount: true,
            canSpecifyTriggerWildcard: true
        },
        {
            providerId: Meevo.Model.MerchantAccountProviderEnum.MeevoPay,
            canInduceTimeoutAfterRequest: true,
        }];
        public providerOptions: IProviderOptions;

        constructor(sectionId: number,
            displayNameContentKey: string,
            template: string,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum,
            protected modalService: Components.Modal.IModalService,
            sectionSecurityKey?: string) {

            super(sectionId, displayNameContentKey, template, false, false, baseEndpoint, sectionEndpoint, loggingService, dataService, identityService, noun, null, sectionSecurityKey);
        }

        sectionModelRetrieved(): void {
            this.providerOptions = _.findWhere(this.providerOptionsConfiguration, { providerId: this.model.merchantProviderId });

            switch (this.model.merchantProviderId) {
                case Model.MerchantAccountProviderEnum.MeevoPay:
                case Model.MerchantAccountProviderEnum.MeevoPayCanada:
                    this.availableActions = this.allAvailableActions.filter(action =>
                        action.value === ProcessorHelperOverrideEnum.Sale ||
                        action.value === ProcessorHelperOverrideEnum.SaleCardOnFile ||
                        action.value === ProcessorHelperOverrideEnum.HostedPagePayment ||
                        action.value === ProcessorHelperOverrideEnum.HostedPagePaymentRetry ||
                        action.value === ProcessorHelperOverrideEnum.HostedPageFundingSource ||
                        action.value === ProcessorHelperOverrideEnum.CreateCardOnFile ||
                        action.value === ProcessorHelperOverrideEnum.Capture);
                    break;

                case Model.MerchantAccountProviderEnum.CareCredit:
                    this.availableActions = this.allAvailableActions.filter(action =>
                        action.value === ProcessorHelperOverrideEnum.Query ||
                        action.value === ProcessorHelperOverrideEnum.HostedPageQuery);
                    break;

                case Model.MerchantAccountProviderEnum.CardConnect:
                    this.availableActions = this.allAvailableActions.filter(action =>
                        action.value === ProcessorHelperOverrideEnum.HostedPageQuery);
                    break;

                default:
                    this.availableActions = this.allAvailableActions;
                    break;
            }

            this.availableResponseOverrides = this.allAvailableResponseOverrides.filter(override => override.providers == null ||
                override.providers.indexOf(this.model.merchantProviderId) > -1);
        }

        addNewTestCase() {
            this.model.testCases.push({
                displayName: "",
                action: ProcessorHelperOverrideEnum.None,
                performProcessorCall: true,
                triggerAmount: null,
                triggerWildcard: null,
                useRequest: {
                    overrides: []
                },
                useResponse: {
                    overrides: []
                }
            });
        }

        deleteTestCase(testCase: ITestCase) {
            var ndx = _.findIndex(this.model.testCases, (item: ITestCase) => {
                return item === testCase;
            });
            this.model.testCases.splice(ndx, 1);
        }

        addNewRequestOverride(testCase: ITestCase) {
            testCase.useRequest.overrides.push({
                key: null,
                value: null
            });
        }

        deleteRequestOverride(testCase: ITestCase, reqOverride: ITestCaseOverride) {
            var ndx = _.findIndex(testCase.useRequest.overrides, (item: ITestCaseOverride) => {
                return item === reqOverride;
            });
            testCase.useRequest.overrides.splice(ndx, 1);
        }

        addNewResponseOverride(testCase: ITestCase) {
            testCase.useResponse.overrides.push({
                key: null,
                value: null
            });
        }

        deleteResponseOverride(testCase: ITestCase, reqOverride: ITestCaseOverride) {
            var ndx = _.findIndex(testCase.useResponse.overrides, (item: ITestCaseOverride) => {
                return item === reqOverride;
            });
            testCase.useResponse.overrides.splice(ndx, 1);
        }

        changeOverride(requestOverride: ITestCaseOverride) {
            requestOverride.value = null;
        }

        getOverrideSettings(availableOverrides:any[], requestOverride: ITestCaseOverride) {
            return _.findWhere(availableOverrides, { name: requestOverride.key });
        }

        isRequestOverrideStringValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableRequestOverrides, requestOverride);
            return setting && setting.dataType == 'string';
        }

        isRequestOverrideNumberValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableRequestOverrides, requestOverride);
            return setting && setting.dataType == 'number';
        }

        isRequestOverrideListValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableRequestOverrides, requestOverride);
            return setting && setting.dataType == 'list';
        }

        getRequestOverrideListValues(requestOverride: ITestCaseOverride) {
            return this.getOverrideSettings(this.availableRequestOverrides, requestOverride).values;
        }

        isResponseOverrideStringValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableResponseOverrides, requestOverride);
            return setting && setting.dataType == 'string';
        }

        isResponseOverrideNumberValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableResponseOverrides, requestOverride);
            return setting && setting.dataType == 'number';
        }

        isResponseOverrideListValue(requestOverride: ITestCaseOverride) {
            let setting = this.getOverrideSettings(this.availableResponseOverrides, requestOverride);
            return setting && setting.dataType == 'list';
        }

        getResponseOverrideListValues(requestOverride: ITestCaseOverride) {
            var overrideList = this.getOverrideSettings(this.availableResponseOverrides, requestOverride);
            return overrideList ? overrideList.values : [];
        }

        listChange(responseOverride) {
            responseOverride.value = responseOverride.listValue !== this.responseOverrideValueOther ? responseOverride.listValue : "";
        }
    }
}